import * as Sentry from '@sentry/react';
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { supabase } from '@/shared/lib/supabase';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { setUserProperties } from '@/shared/lib/analytics';
import { mapDbSubscription } from '../types/database';
import { FREE_PAGE_LIMIT } from '../types';
import type { DbSubscription } from '../types/database';
import type { Subscription } from '../types';

interface SubscriptionContextType {
  subscription: Subscription | null;
  isLoading: boolean;
  error: string | null;
  isPro: boolean;
  isInGracePeriod: boolean;
  gracePeriodDaysRemaining: number;
  pageCount: number;
  canCreatePage: boolean;
  canUsePremiumTemplate: (isPremium: boolean) => boolean;
  incrementPageCount: () => void;
  decrementPageCount: () => void;
  refetch: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

interface SubscriptionProviderProps {
  children: ReactNode;
}

export function SubscriptionProvider({ children }: SubscriptionProviderProps) {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!user) {
      setSubscription(null);
      setPageCount(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      // Fetch subscription and page count in parallel
      const [subResult, countResult] = await Promise.all([
        supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .single(),
        supabase
          .from('pages')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id),
      ]);

      if (subResult.error) {
        console.error('Failed to fetch subscription:', subResult.error.message);
        Sentry.captureException(
          new Error(subResult.error.message),
          { tags: { operation: 'fetch_subscription' }, extra: { ...subResult.error } }
        );
      } else if (subResult.data) {
        const mapped = mapDbSubscription(subResult.data as DbSubscription);
        setSubscription(mapped);
        setUserProperties({ plan: mapped.plan, subscription_status: mapped.status });
      }

      if (countResult.error) {
        console.error('Failed to fetch page count:', countResult.error.message);
        Sentry.captureException(
          new Error(countResult.error.message),
          { tags: { operation: 'fetch_page_count' }, extra: { ...countResult.error } }
        );
      }

      setPageCount(countResult.count ?? 0);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch subscription data';
      console.error('Failed to fetch subscription data:', err);
      Sentry.captureException(err, { tags: { operation: 'fetch_subscription' } });
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const isInGracePeriod =
    subscription?.plan === 'pro' &&
    subscription?.status === 'past_due' &&
    !!subscription?.gracePeriodEndsAt &&
    new Date(subscription.gracePeriodEndsAt) > new Date();

  const gracePeriodDaysRemaining =
    isInGracePeriod && subscription?.gracePeriodEndsAt
      ? Math.ceil(
          (new Date(subscription.gracePeriodEndsAt).getTime() - Date.now()) /
            (1000 * 60 * 60 * 24)
        )
      : 0;

  const isPro =
    subscription?.plan === 'pro' &&
    (['active', 'trialing'].includes(subscription?.status ?? '') ||
      isInGracePeriod);

  const canCreatePage = isPro || pageCount < FREE_PAGE_LIMIT;

  const canUsePremiumTemplate = (isPremiumTemplate: boolean) => {
    if (!isPremiumTemplate) return true;
    return isPro;
  };

  const incrementPageCount = () => setPageCount((c) => c + 1);
  const decrementPageCount = () => setPageCount((c) => Math.max(0, c - 1));

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        isLoading,
        error,
        isPro,
        isInGracePeriod,
        gracePeriodDaysRemaining,
        pageCount,
        canCreatePage,
        canUsePremiumTemplate,
        incrementPageCount,
        decrementPageCount,
        refetch: fetchData,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscriptionContext() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error(
      'useSubscriptionContext must be used within SubscriptionProvider'
    );
  }
  return context;
}
